'use client';

import { Box, Typography } from '@mui/material';
import Link from 'next/link';
import styled from '@emotion/styled';
import { color, spacing } from '@/theme/tokens';
import { breakpoints } from '@/theme/tokens';
import { useEntraAuth } from '@/components/hooks/useEntraAuth';
import { useParams } from 'next/navigation';

const MDXLink = styled(Link)`
    color: ${color.brand[50]};
`;

interface MDXComponentsOptions {
    customLink?: boolean;
    h1Image?: React.ReactNode;
    paragraphMargin?: {
        base?: string;
        sm?: string;
        md?: string;
    };
    listMargin?: {
        base?: string;
        sm?: string;
        md?: string;
    };
}

export const CustomLink = ({ href, children, ...props }: any) => {
    const { isAuthenticated } = useEntraAuth();
    const params = useParams();
    const locale = params.locale as string;

    if (href === '/title-check-info') {
        if (isAuthenticated) {
            return (
                <MDXLink target="_blank" href={`/${locale}/customer/check/info`} {...props}>
                    <Typography
                        variant="paragraph4semibold"
                        sx={{
                            textDecoration: 'underline',
                            color: 'inherit',
                        }}
                    >
                        {children}
                    </Typography>
                </MDXLink>
            );
        } else {
            return (
                <Typography
                    component="span"
                    variant="paragraph4semibold"
                    sx={{
                        color: color.brand[50],
                        textDecoration: 'underline',
                    }}
                    {...props}
                >
                    {children}
                </Typography>
            );
        }
    }

    if (href === '/contacts') {
        return (
            <MDXLink target="_blank" href={`/${locale}/contacts`} {...props}>
                <Typography
                    variant="paragraph4regular"
                    sx={{
                        textDecoration: 'underline',
                        color: 'inherit',
                    }}
                >
                    {children}
                </Typography>
            </MDXLink>
        );
    }

    if (href === '/gg-autosales') {
        return (
            <MDXLink target="_blank" href={`/${locale}/gg-autosales`} {...props}>
                <Typography
                    variant="paragraph4regular"
                    sx={{
                        textDecoration: 'underline',
                        color: 'inherit',
                    }}
                >
                    {children}
                </Typography>
            </MDXLink>
        );
    }

    // Prepend locale to internal paths so links stay within the current locale
    const resolvedHref =
        typeof href === 'string' && href.startsWith('/') && !href.startsWith('//') ? `/${locale}${href}` : href;

    return (
        <MDXLink target="_blank" href={resolvedHref} {...props}>
            <Typography
                variant="paragraph4regular"
                sx={{
                    textDecoration: 'underline',
                    color: 'inherit',
                }}
            >
                {children}
            </Typography>
        </MDXLink>
    );
};

// Fast Refresh ergonomics only, no runtime effect; splitting the file would churn every importer.
// react-doctor-disable-next-line only-export-components
export const getMDXComponents = (options: MDXComponentsOptions = {}) => {
    const {
        customLink = true,
        h1Image,
        paragraphMargin = {
            base: spacing[16],
            sm: spacing[20],
            md: spacing[24],
        },
        listMargin = {
            base: spacing[16],
            sm: spacing[20],
            md: spacing[24],
        },
    } = options;

    return {
        BrandNote: (props: any) => (
            <Box
                component="div"
                sx={{
                    color: color.brand[50],
                    marginTop: spacing[24],
                    [`@media (max-width: ${breakpoints.md})`]: {
                        marginTop: spacing[20],
                    },
                    [`@media (max-width: ${breakpoints.sm})`]: {
                        marginTop: spacing[16],
                    },
                    fontFamily: 'var(--font-noto-sans), Helvetica, Arial, sans-serif',
                    '& *, & .MuiTypography-root': { color: 'inherit' },
                    '& p': {
                        fontSize: '14px ',
                    },
                }}
                {...props}
            />
        ),
        // h1 in MDX renders as h2 with heading2bold style (since we already have h1 in all the pages)
        h1: (props: any) => (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: spacing[0],
                    width: '100%',
                    // Hide image by default
                    '& .mdx-h2-image': {
                        display: 'none',
                    },
                    // Show image only for the first h2 wrapper
                    '&:first-of-type .mdx-h2-image': {
                        display: 'block',
                    },
                }}
            >
                <Typography component="h2" variant="heading2bold" color={color.gray[80]} sx={{ '& a': { color: 'inherit' } }} {...props} />
                {h1Image && <Box className="mdx-h2-image">{h1Image}</Box>}
            </Box>
        ),

        // h2 in MDX renders as h3, keeps heading1bold style
        h2: (props: any) => (
            <Typography
                component="h3"
                variant="heading1bold"
                color={color.gray[80]}
                sx={{
                    mb: spacing[8],
                    '& a': { color: 'inherit' },
                }}
                {...props}
            />
        ),

        // h3 in MDX renders as h4, keeps heading4medium style
        h3: (props: any) => (
            <Typography
                component="h4"
                variant="heading4medium"
                color={color.gray[80]}
                sx={{
                    mb: spacing[12],
                    mt: spacing[24],
                    '& a': { color: 'inherit' },
                }}
                {...props}
            />
        ),

        p: (props: any) => (
            <Typography
                component="p"
                variant="paragraph4regular"
                color={color.gray[80]}
                sx={{
                    mb: paragraphMargin.base,
                    ...(paragraphMargin.sm && {
                        [`@media (min-width: ${breakpoints.sm})`]: {
                            mb: paragraphMargin.sm,
                        },
                    }),
                    ...(paragraphMargin.md && {
                        [`@media (min-width: ${breakpoints.md})`]: {
                            mb: paragraphMargin.md,
                        },
                    }),
                    '&:has(+ ul), &:has(+ ol)': {
                        mb: `${spacing[8]} !important`,
                        [`@media (min-width: ${breakpoints.sm})`]: {
                            mb: `${spacing[8]} !important`,
                        },
                        [`@media (min-width: ${breakpoints.md})`]: {
                            mb: `${spacing[8]} !important`,
                        },
                    },
                    '&:last-of-type': {
                        mb: 0,
                    },
                }}
                {...props}
            />
        ),
        ul: (props: any) => (
            <Box
                component="ul"
                sx={{
                    listStyle: 'disc',
                    padding: 0,
                    margin: 0,
                    marginBottom: listMargin.base,
                    [`@media (min-width: ${breakpoints.sm})`]: {
                        ...(listMargin.sm && {
                            marginBottom: listMargin.sm,
                        }),
                    },
                    ...(listMargin.md && {
                        [`@media (min-width: ${breakpoints.md})`]: {
                            marginBottom: listMargin.md,
                        },
                    }),
                    '& li': {
                        lineHeight: 1.5,
                        marginInlineStart: 'calc(1.5 * 1em)',
                    },
                }}
                {...props}
            />
        ),

        ol: (props: any) => (
            <Box
                component="ol"
                sx={{
                    listStyle: 'decimal',
                    pl: spacing[24],
                    mb: listMargin.base,
                    [`@media (min-width: ${breakpoints.sm})`]: {
                        ...(listMargin.sm && {
                            mb: listMargin.sm,
                        }),
                    },
                    ...(listMargin.md && {
                        [`@media (min-width: ${breakpoints.md})`]: {
                            mb: listMargin.md,
                        },
                    }),
                }}
                {...props}
            />
        ),
        li: ({ children, ...props }: any) => (
            <Typography
                component="li"
                variant="paragraph4regular"
                sx={{
                    '&:not(:last-child)': {
                        mb: spacing[4],
                    },
                }}
                {...props}
            >
                {children}
            </Typography>
        ),
        a: ({ href, children, ...props }: any) => {
            if (customLink) {
                return (
                    <CustomLink href={href} {...props}>
                        {children}
                    </CustomLink>
                );
            }
            return (
                <MDXLink href={href} {...props}>
                    <Typography
                        variant="paragraph4regular"
                        sx={{
                            textDecoration: 'underline',
                            color: 'inherit',
                        }}
                    >
                        {children}
                    </Typography>
                </MDXLink>
            );
        },
        strong: (props: any) => <Typography component="strong" variant="paragraph4semibold" {...props} />,
    };
};
